/*globals STATIC_URL*/
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  getFormattedDateWithTime,
  rupees,
  getFullName,
  isSmallDevice,
  getUrlParameterByName,
} from 'config/utils';
import Loading from 'views/Loading';
import { Row, Columns } from 'views/Grid';
import Rupee from 'views/Rupee';
import FailureResponse from './FailureResponse';
import DigitalFileProtection from './DigitalFileProtection';
import CartItemFull from 'cart/views/full/CartItemFull';
import ia from 'config/Analytics';

import {
  handlePostPurchaseDataUpdate,
  retryFailedPayment,
} from '../../actions/PostPurchaseActions';

const Tick = ({ tickImage, className }) => (
  <img className={className} src={tickImage} alt="success"/>
);

const PaymentSuccessMessage = () => (
  <h3 className="text-green font-rc">Payment Successful!</h3>
);

const Divider = () => (
  <hr style={{ margin: '10px 0' }}/>
);

const PaymentSummary = ({ payment }) => (
  <table className="table-compact table-vertical-center table-colorless text-medium" style={{ width: '100%' }}>
    <tbody>
      <tr>
        <td width="90px" className="text-bold">Payment ID</td>
        <td className="float--right">{payment.paymentId}</td>
      </tr>
      { !payment.isFree ?
          <tr>
            <td className="text-bold">Amount</td>
            <td className="float--right"><Rupee value={rupees(payment.link.amount)} /></td>
          </tr> : null
      }
      <tr>
        <td className="text-bold">Paid to</td>
        <td title={payment.seller.fullname} width="210px" className="float--right right text-ellipsis">{payment.seller.fullname}</td>
      </tr>
      <tr>
        <td className="text-bold">Paid on</td>
        <td className="float--right">{getFormattedDateWithTime(new Date(payment.date))}</td>
      </tr>
    </tbody>
  </table>
);

const ReceiptDetails = ({ payment }) => (
  <p className="break-long-words">
    We've sent the receipt to {payment.buyer.email} & {payment.buyer.phone}
  </p>
);

const CaseDetails = ({ payment }) => (
  <div>
    { payment.case ?
      <p className="soft--bottom">
        For any problems, <a href={payment.case.url} target="_blank">create a case</a>.
      </p> :
      <p className="soft--bottom">
        For questions related to payments, <a href="mailto:support@instamojo.com" target="_blank">support@instamojo.com</a>.
      </p>
    }
  </div>
);

const ShareIcon = ({
  anchorLink,
  seller: { username, email },
  shareMode,
}) => (
  <a href={anchorLink}
    target="_blank"
    className="push--sides"
    onClick={() => {
      ia.sendEvent('Shared Payment Success', {
        'share_mode' : shareMode,
        'seller_username' : username,
        'seller_email' : email,
      });
    }}>
    <img
      src={`${STATIC_URL}assets/images/share/${shareMode}-new.png`}
      style={{ maxWidth: '32px' }} />
  </a>
);

const PostTransactionInterstitial = ({ payment }) => {
  const {
    postTransactionInterstitial: {
      showPostTransactionInterstitial,
      publicWebsite: publicWebsiteAddress,
      twitterHandle: twitterHandleName,
    } = {},
    seller: {
      fullname,
      username,
    },
    seller,
  } = payment;

  const shareName = getFullName({ name: fullname, username });

  const twitterHandle = twitterHandleName ? `@${twitterHandleName}` : shareName;
  const twitterLink = `https://twitter.com/intent/tweet?text=I%20just%20paid%20${ twitterHandle }%20via%20@instamojo%20%23digitalindia`;

  const facebookAppID = '1767574003460647';
  const facebookName = 'I PAID ONLINE %23DIGITALINDIA';
  const facebookDescription = `I just paid ${ shareName } via www.instamojo.com. Instamojo made paying online easier than ever. %23digitalindia`;
  const facebookImage = 'https://www.instamojo.com/blog/wp-content/uploads/2017/04/fb-share.png';
  const facebookCaption = 'Start using Instamojo to bring digital India dream come true.';
  const facebookURL = 'www.instamojo.com/?utm_source=fb-share-post-purchase';
  const facebookLink = `https://www.facebook.com/dialog/feed?app_id=${ facebookAppID }&link=${ facebookURL }&picture=${ facebookImage }&name=${ facebookName }&caption=${ facebookCaption }&description=${ facebookDescription }&redirect_uri=http://www.facebook.com`;

  const publicWebsite = publicWebsiteAddress ? `(${publicWebsiteAddress}) ` : '';
  const whatsappLink = !isSmallDevice() ? `https://web.whatsapp.com/send?text=I%20just%20paid%20${ shareName }%20${ publicWebsite }via%20https%3A%2F%2Fwww.instamojo.com` : `whatsapp://send?text=I%20just%20paid%20${ shareName }%20${ publicWebsite }via%20https%3A%2F%2Fwww.instamojo.com`;
  const emailLink = `mailto:?subject=${encodeURIComponent('I just paid online via Instamojo')}&body=${encodeURIComponent(`Instamojo made paying online easier than ever. I just paid ${ shareName } ${ publicWebsite }via www.instamojo.com`)}`;

  return (
    showPostTransactionInterstitial ?
    <div>
      <Divider />
      <div>
        <p className="text-center soft--bottom"><b>Share your purchase experience with your friends:</b></p>
        <div className="text-center soft--bottom">
          <ShareIcon
            seller={seller}
            anchorLink={facebookLink}
            shareMode="facebook"
          />
          <ShareIcon
            seller={seller}
            anchorLink={twitterLink}
            shareMode="twitter"
          />
          <ShareIcon
            seller={seller}
            anchorLink={whatsappLink}
            shareMode="whatsapp"
          />
          <ShareIcon
            seller={seller}
            anchorLink={emailLink}
            shareMode="email"
          />
        </div>
      </div>
    </div> :
    null
  );
};

const SuccessResponse = ({ payment, tickImage, handlePostPurchaseDataUpdate }) => {
  const {
    redirect,
    upload,
    note,
    downloadLink,
    isDownloadProtected,
  } = payment.offer;
  return (
    (
      <div className="font-rc">
        <div className="center">
          {
            redirect ?
            <Loading className="pace-loading huge slow"/> :
            <Tick tickImage={tickImage} className="soft--ends" />
          }
          <PaymentSuccessMessage />
          {
            redirect ?
            <p className="text-bold"> Please wait while we redirect you... </p> :
            null
          }
        </div>

        <Divider />

        <PaymentSummary payment={payment} />

        <Divider />

        <div className="soft-half--sides text-medium">
          { !upload ?
            <ReceiptDetails payment={payment} /> :
            null
          }
          { note ?
            <div>
              <label className="text-bold">Note</label>
              <div className="yellow-message-block push-half--bottom">
                <p style={{ fontSize: '14px' }}>{note}</p>
              </div>
            </div> :
            null
          }
          { upload ?
            <a
              href={!isDownloadProtected ? downloadLink : '#'}
              onClick={(e) => {
                if (isDownloadProtected) {
                  e.preventDefault();
                  handlePostPurchaseDataUpdate('isVerifyingBuyerForFileDownload', true);
                }
              }}
              className="btn btn-green btn-block push--ends">
              Download
            </a> : null
          }
          <CaseDetails payment={payment} />
          <PostTransactionInterstitial payment={payment}  />
        </div>
      </div>
    )
  );
};


const CartSuccessResponse = ({ payment, tickImage }) => (
  <div className="font-rc">
    <Row className="soft-half--top">
      <Columns className="small-12 medium-4">
        <div className="media soft--ends">
          <Tick className="media__img" tickImage={tickImage} />
          <div className="media__body">
            <h5 className="text-green font-rc push-half--top">Payment Successful</h5>
          </div>
        </div>
      </Columns>
      <Columns className="small-12 medium-8">
        <PaymentSummary payment={payment} />
      </Columns>
    </Row>

    <Divider />

    <ul className='cart--items full push-double--bottom'>
      { payment.subOrders.map((order, index) =>
        <li key={index}>
          <CartItemFull
            readOnly={true}
            {...order}
          />
        </li>
      )}
    </ul>

    <div className="soft--left">
      <ReceiptDetails payment={payment} />
      <CaseDetails payment={payment} />
    </div>
  </div>
);

const CartFailureResponse = ({ payment }) => (
  <Row>
    <Columns className="small-12">
      <FailureResponse payment={payment} />
    </Columns>
  </Row>
);

class PostPurchaseResponse extends React.Component {
  componentDidMount () {
    if (this.props.payment.offer.upload && (getUrlParameterByName('expired') === 'true')) {
      this.props.handlePostPurchaseDataUpdate('isVerifyingBuyerForFileDownload', true);
    }
    if (this.props.shouldShowPaymentOptionsDirectlyOnRetry) {
      this.props.retryFailedPayment();
    }
  }

  render () {
    const {
      isCartOrder,
      status,
      payment,
      tickImage,
      isVerifyingBuyerForFileDownload,
    } = this.props;

    return (
      <div className="soft-half" style={{ background: '#fff', borderRadius: '0 0 4px 4px' }}>
        { !isVerifyingBuyerForFileDownload ?
          <div>
            { status === 'success' ?
              isCartOrder ?
              <CartSuccessResponse payment={payment} tickImage={tickImage} /> :
              <SuccessResponse
                payment={payment}
                tickImage={tickImage}
                handlePostPurchaseDataUpdate={(formField, formFieldData) => this.props.handlePostPurchaseDataUpdate(formField, formFieldData)}
              /> :
              isCartOrder ?
              <CartFailureResponse payment={payment} /> :
              <FailureResponse payment={payment} />
            }
          </div> :
          <DigitalFileProtection />
        }
      </div>
    );
  }
}

PostPurchaseResponse.propTypes = {
  status: PropTypes.string.isRequired,
  tickImage: PropTypes.string.isRequired,
  isCartOrder: PropTypes.bool.isRequired,
  payment: PropTypes.shape({
    link: PropTypes.shape({
      amount: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
    }).isRequired,
    paymentId: PropTypes.string.isRequired,
    seller: PropTypes.shape({
      fullname: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }).isRequired,
    buyer: PropTypes.shape({
      email: PropTypes.string.isRequired,
    }),
    subOrders: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
        quantity: PropTypes.number.isRequired,
        amount: PropTypes.number.isRequired,
        variants: PropTypes.arrayOf(PropTypes.shape({
          categoryName: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
        })).isRequired,
        customFields: PropTypes.arrayOf(PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })).isRequired,
        shipsIn: PropTypes.number,
      })
    ),
  }),
};

export default connect(() => ({}), {
  handlePostPurchaseDataUpdate,
  retryFailedPayment,
})(PostPurchaseResponse);



// WEBPACK FOOTER //
// ./src/payments/views/post-purchase/PostPurchaseResponse.js